/**
 * LoopDiffBlock — what changed since the last pass of the same ball.
 *
 * Three homes, one block: the executive inbox above a proposal that came back,
 * the loop desk when a round opens, and the exchange timeline under each pass
 * after the first. The database function `catchball_loop_diff` (migration 822)
 * does the comparing; this only shows it, in reading order: the sentence, the
 * ranking movement across the line, what the request now says differently,
 * what the proposal now says differently, and who decided the earlier pass
 * with the note they left. Hidden when there is nothing earlier to compare
 * with. Never a score: the round count appears only inside the pass label.
 * Roadmap C-1; PRD docs/9-exploration/PRD-loop-diff.md.
 */

import { Panel, DataTable, Disclosure, type Column } from '@/components/ds';
import { useLoopDiff } from '../../../hooks/useLoopDiff';
import {
  changedRankingRows,
  loopDiffHasReference,
  rankingChangeLabel,
  referenceLine,
  requestFieldLabel,
} from '../../../lib/catchball/loopDiff';
import type {
  LoopDiff,
  LoopDiffRankingRow,
  LoopDiffReferenceKind,
  LoopDiffRequestField,
} from '../../../types/prioritization/loopDiff';

export type LoopDiffVariant = 'inbox' | 'request' | 'timeline';

export interface LoopDiffBlockProps {
  loopId: string;
  /** Compare this pass (a proposal that went up). */
  proposalId?: string | null;
  /** Or compare this turn of the loop. */
  turn?: number | null;
  variant: LoopDiffVariant;
  className?: string;
}

function eyebrow(kind: LoopDiffReferenceKind, variant: LoopDiffVariant): string {
  if (variant === 'request') return 'What changed in the request';
  switch (kind) {
    case 'turn':
      return 'Since it was returned';
    case 'plan':
      return 'Since the plan was ratified';
    default:
      return 'Since the last round';
  }
}

function formatDay(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const RANKING_COLUMNS: Column<LoopDiffRankingRow>[] = [
  { key: 'title', header: 'Item', render: (row) => row.title },
  { key: 'before', header: 'Was', numeric: true, width: '5rem', render: (row) => (row.before == null ? '—' : String(row.before)) },
  { key: 'after', header: 'Now', numeric: true, width: '5rem', render: (row) => (row.after == null ? '—' : String(row.after)) },
  { key: 'change', header: 'What happened', render: (row) => rankingChangeLabel(row) },
];

function RankingPart({ diff }: { diff: LoopDiff }) {
  const ranking = diff.ranking;
  if (!ranking) return null;
  const rows = changedRankingRows(diff);
  return (
    <div className="space-y-2" data-testid="loop-diff-ranking">
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--text-sec)]">The order did not change.</p>
      ) : (
        <DataTable<LoopDiffRankingRow>
          columns={RANKING_COLUMNS}
          rows={rows}
          getRowKey={(row) => row.item_id}
          density="compact"
          bordered={false}
          caption="Items that are not in the same place as before"
          captionHidden
        />
      )}
      <p className="text-xs text-[var(--text-ter)]">
        {ranking.cut_known
          ? `Cut line at ${ranking.cut_after}${ranking.unchanged_top > 0 ? `; top ${ranking.unchanged_top} unchanged` : ''}.`
          : 'No cut line on either side, so crossings are not shown.'}
      </p>
    </div>
  );
}

function RequestField({ field }: { field: LoopDiffRequestField }) {
  const label = requestFieldLabel(field.field);
  if (field.kind === 'list') {
    return (
      <li className="text-sm">
        <span className="font-medium text-[var(--text-pri)]">{label}</span>
        <ul className="mt-1 space-y-1">
          {(field.added ?? []).map((v, i) => (
            <li key={`a${i}`} className="flex gap-2 text-[var(--text-sec)]">
              <span className="shrink-0 text-[var(--green)]" aria-label="added">+</span>
              <span>{v}</span>
            </li>
          ))}
          {(field.removed ?? []).map((v, i) => (
            <li key={`r${i}`} className="flex gap-2 text-[var(--text-ter)] line-through">
              <span className="shrink-0 no-underline text-[var(--coral)]" aria-label="removed">−</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </li>
    );
  }
  if (field.kind === 'text') {
    const before = typeof field.before === 'string' && field.before.trim() ? field.before : null;
    const after = typeof field.after === 'string' && field.after.trim() ? field.after : null;
    return (
      <li className="text-sm">
        <span className="font-medium text-[var(--text-pri)]">{label}</span>
        <div className="mt-1 space-y-1 text-[var(--text-sec)]">
          {before && (
            <p>
              <span className="text-xs uppercase tracking-wide text-[var(--text-ter)] mr-2">Before</span>
              <span className="line-through text-[var(--text-ter)]">{before}</span>
            </p>
          )}
          <p>
            <span className="text-xs uppercase tracking-wide text-[var(--text-ter)] mr-2">Now</span>
            {after ?? <span className="italic">removed</span>}
          </p>
        </div>
      </li>
    );
  }
  return (
    <li className="text-sm">
      <span className="font-medium text-[var(--text-pri)]">{label}</span>
      <span className="ml-2 text-[var(--text-sec)]">changed</span>
    </li>
  );
}

function RequestPart({ diff, sayUnchanged }: { diff: LoopDiff; sayUnchanged: boolean }) {
  const request = diff.request;
  if (!request) return null;
  if (!request.changed) {
    return sayUnchanged ? (
      <p className="text-sm text-[var(--text-sec)]" data-testid="loop-diff-request">The request did not change.</p>
    ) : null;
  }
  return (
    <ul className="space-y-3" data-testid="loop-diff-request">
      {request.fields.map((field) => (
        <RequestField key={field.field} field={field} />
      ))}
    </ul>
  );
}

function ProposalPart({ diff }: { diff: LoopDiff }) {
  const proposal = diff.proposal;
  if (!proposal) return null;
  const { rationale, tradeoffs } = proposal;
  if (!rationale.changed && !tradeoffs.changed) return null;
  return (
    <div className="space-y-2" data-testid="loop-diff-proposal">
      {rationale.changed && (
        <Disclosure title="Rationale, before and after">
          <div className="grid gap-3 md:grid-cols-2 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--text-ter)] mb-1">Before</p>
              <p className="text-[var(--text-ter)] whitespace-pre-line">{rationale.before ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--text-ter)] mb-1">Now</p>
              <p className="text-[var(--text-sec)] whitespace-pre-line">{rationale.after ?? '—'}</p>
            </div>
          </div>
        </Disclosure>
      )}
      {tradeoffs.changed && (
        <Disclosure title="Tradeoffs, before and after">
          <div className="grid gap-3 md:grid-cols-2 text-xs font-mono">
            <pre className="whitespace-pre-wrap text-[var(--text-ter)]">{JSON.stringify(tradeoffs.before ?? null, null, 2)}</pre>
            <pre className="whitespace-pre-wrap text-[var(--text-sec)]">{JSON.stringify(tradeoffs.after ?? null, null, 2)}</pre>
          </div>
        </Disclosure>
      )}
    </div>
  );
}

function WhoAndWhyPart({ diff }: { diff: LoopDiff }) {
  const ref = diff.reference;
  if (!ref) return null;
  const who = ref.decided_by_name ? ` by ${ref.decided_by_name}` : '';
  const when = ref.decided_at ? ` on ${formatDay(ref.decided_at)}` : '';
  const current = diff.current;
  // The note is shown once (owner ruling 2026-10-14). The sentence above
  // already quotes it whenever there is one, so the quote here appears only if
  // a sentence ever arrives without it.
  const noteInSentence = Boolean(ref.note && diff.sentence?.includes(ref.note));
  return (
    <div className="space-y-1 text-sm" data-testid="loop-diff-who">
      <p className="text-[var(--text-sec)]">
        {ref.label.charAt(0).toUpperCase() + ref.label.slice(1)} was {ref.decision_word}{who}{when}.
      </p>
      {ref.note && !noteInSentence && (
        <blockquote className="border-l-2 border-[var(--rule)] pl-3 text-[var(--text-pri)] whitespace-pre-line">{ref.note}</blockquote>
      )}
      {current.submitted_at && (
        <p className="text-xs text-[var(--text-ter)]">
          This pass{current.submitted_by_name ? ` by ${current.submitted_by_name}` : ''} on {formatDay(current.submitted_at)}.
        </p>
      )}
    </div>
  );
}

export function LoopDiffBlock({ loopId, proposalId, turn, variant, className }: LoopDiffBlockProps) {
  const { diff, isLoading } = useLoopDiff({ loopId, proposalId, turn });
  if (isLoading || !loopDiffHasReference(diff)) return null;
  const ref = diff.reference!;
  const label = eyebrow(ref.kind, variant);

  const body =
    variant === 'request' ? (
      <div className="space-y-4">
        <RequestPart diff={diff} sayUnchanged />
        <WhoAndWhyPart diff={diff} />
      </div>
    ) : (
      <div className="space-y-4">
        <RankingPart diff={diff} />
        <RequestPart diff={diff} sayUnchanged={false} />
        <ProposalPart diff={diff} />
        <WhoAndWhyPart diff={diff} />
      </div>
    );

  return (
    <div className={className} data-testid="loop-diff" data-reference-kind={ref.kind}>
      <Panel label={label} inset={variant === 'timeline'}>
        <div className="space-y-3">
          {diff.sentence && (
            <p className="text-sm text-[var(--text-pri)] leading-relaxed" data-testid="loop-diff-sentence">
              {diff.sentence}
            </p>
          )}
          <p className="text-xs text-[var(--text-ter)]">{referenceLine(diff)}</p>
          {variant === 'timeline' ? (
            <Disclosure title="What changed">{body}</Disclosure>
          ) : (
            body
          )}
        </div>
      </Panel>
    </div>
  );
}
